import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { X, Flame, ChevronRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const SESSION_KEY = "hylas_loyalty_banner_dismissed";
const ACCENT = "#FF4D00";

export default function LoyaltyBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem(SESSION_KEY)) return;
    const timer = setTimeout(() => setVisible(true), 3000);
    return () => clearTimeout(timer);
  }, []);

  function dismiss() {
    sessionStorage.setItem(SESSION_KEY, "1");
    setVisible(false);
  }

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="loyalty-banner"
          role="banner"
          aria-label="Loyalty Program announcement"
          initial={{ opacity: 0, y: 32, x: -8 }}
          animate={{ opacity: 1, y: 0, x: 0 }}
          exit={{ opacity: 0, y: 20, x: -8, transition: { duration: 0.22 } }}
          transition={{ type: "spring", stiffness: 340, damping: 28, mass: 0.8 }}
          className="fixed bottom-5 left-5 z-[9000] w-[clamp(260px,88vw,340px)]"
          style={{ filter: `drop-shadow(0 8px 32px rgba(255,77,0,0.28))` }}
        >
          <div
            className="relative overflow-hidden flex flex-col gap-0"
            style={{
              background: "linear-gradient(135deg, #111111 0%, #150b05 100%)",
              border: `1px solid rgba(255,77,0,0.35)`,
            }}
          >
            {/* Accent top bar */}
            <div style={{ height: 2, background: `linear-gradient(90deg, ${ACCENT}, #FF8C00)` }} />

            {/* Shimmer sweep */}
            <motion.div
              animate={{ x: ["-120%", "220%"] }}
              transition={{ duration: 2.8, repeat: Infinity, ease: "linear", repeatDelay: 4 }}
              className="absolute inset-y-0 w-1/2 pointer-events-none"
              style={{ background: `linear-gradient(90deg, transparent, rgba(255,77,0,0.06), transparent)` }}
            />

            <div className="flex items-center gap-3 px-4 py-3">
              {/* Icon */}
              <div
                className="w-8 h-8 flex items-center justify-center shrink-0"
                style={{ background: `rgba(255,77,0,0.12)`, border: `1px solid rgba(255,77,0,0.3)` }}
              >
                <Flame className="w-4 h-4" style={{ color: ACCENT }} />
              </div>

              {/* Text */}
              <div className="flex-1 min-w-0">
                <p className="font-head text-[10px] font-black uppercase tracking-[0.15em] leading-tight" style={{ color: ACCENT }}>
                  Driver Loyalty Program
                </p>
                <p className="text-[10px] text-white/45 mt-0.5 leading-snug">
                  Earn $100+ in tracked bonuses — docs, drug screen &amp; hire plans.
                </p>
              </div>

              {/* Dismiss */}
              <button
                onClick={dismiss}
                data-testid="loyalty-banner-dismiss"
                aria-label="Dismiss"
                className="shrink-0 w-5 h-5 flex items-center justify-center text-white/25 hover:text-white/55 transition-colors self-start mt-0.5"
              >
                <X className="w-3 h-3" />
              </button>
            </div>

            {/* CTA row */}
            <div style={{ borderTop: "1px solid rgba(255,77,0,0.12)" }} className="flex">
              <Link
                to="/loyalty"
                onClick={dismiss}
                data-testid="loyalty-banner-cta"
                className="flex-1 flex items-center justify-center gap-1.5 font-head text-[10px] uppercase tracking-[0.15em] py-2.5 transition-all hover:brightness-110"
                style={{ background: ACCENT, color: "white" }}
              >
                See How It Works <ChevronRight className="w-3 h-3" />
              </Link>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
